import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { motion } from 'framer-motion';

// ─── Types ──────────────────────────────────────────────────────────
export interface SupporterTrendDatum {
  date: string;
  newSupporters: number;
  repeatSupporters: number;
}

export interface SupporterTrendChartProps {
  data: SupporterTrendDatum[];
}

// ─── Custom Tooltip ─────────────────────────────────────────────────
interface TooltipPayloadItem {
  value: number;
  dataKey: string;
  color: string;
  payload: SupporterTrendDatum;
}

const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
}) => {
  if (!active || !payload?.length) return null;

  const datum = payload[0].payload;
  const total = datum.newSupporters + datum.repeatSupporters;

  return (
    <div className="rounded-lg border border-white/10 bg-black/80 px-4 py-3 shadow-lg backdrop-blur-sm">
      <p className="mb-2 text-xs font-medium text-gray-400">{datum.date}</p>
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full bg-neon-blue" />
          <span className="text-xs text-gray-300">新規</span>
          <span className="ml-auto text-sm font-bold text-neon-blue">
            {datum.newSupporters}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full bg-pink-500" />
          <span className="text-xs text-gray-300">リピート</span>
          <span className="ml-auto text-sm font-bold text-pink-500">
            {datum.repeatSupporters}
          </span>
        </div>
        <div className="mt-1 border-t border-white/10 pt-1">
          <span className="text-xs text-gray-400">合計 </span>
          <span className="text-xs font-bold text-white">{total}人</span>
        </div>
      </div>
    </div>
  );
};

// ─── Custom Legend ──────────────────────────────────────────────────
const CustomLegend = () => (
  <div className="mt-2 flex items-center justify-center gap-5">
    <div className="flex items-center gap-1.5">
      <span className="inline-block h-2.5 w-2.5 rounded-sm bg-neon-blue" />
      <span className="text-xs text-gray-400">新規サポーター</span>
    </div>
    <div className="flex items-center gap-1.5">
      <span className="inline-block h-2.5 w-2.5 rounded-sm bg-pink-500" />
      <span className="text-xs text-gray-400">リピーター</span>
    </div>
  </div>
);

// ─── Component ──────────────────────────────────────────────────────
export const SupporterTrendChart = ({ data }: SupporterTrendChartProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="w-full"
    >
      <div className="rounded-xl border border-white/10 bg-black/30 p-5">
        <h3 className="mb-4 text-sm font-bold tracking-widest text-gray-400">
          サポーター推移
        </h3>

        <div className="h-[280px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              barCategoryGap="20%"
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.05)"
                vertical={false}
              />

              <XAxis
                dataKey="date"
                tick={{ fill: '#9ca3af', fontSize: 11 }}
                axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                tickLine={false}
                dy={8}
              />

              <YAxis
                tick={{ fill: '#9ca3af', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                width={32}
              />

              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'rgba(255,255,255,0.03)' }}
              />

              <Legend content={<CustomLegend />} />

              <Bar
                dataKey="newSupporters"
                stackId="supporters"
                fill="#00f3ff"
                radius={[0, 0, 0, 0]}
              />
              <Bar
                dataKey="repeatSupporters"
                stackId="supporters"
                fill="#ec4899"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </motion.div>
  );
};
